import i18n from 'i18n';
import { formatWeightWithUnit } from './formatter';

const t = i18n.t.bind(i18n);

/**
 * Витрина: цена «за 1 кг» только если единица продажи из 1С — килограмм (`main_unit`).
 * У штучных в `unit` часто тоже «кг» (учётная единица), но продажа в упаковке — на витрине «за уп.».
 */
const isWeightPricedProduct = item => {
	const mainRaw = item && item.main_unit;
	const main = typeof mainRaw === 'string' ? mainRaw.toLowerCase().trim() : '';
	if (main.includes('кг')) return true;
	if (main.includes('шт') || main.includes('уп') || main.includes('лк')) {
		return false;
	}
	const u = item && item.unit;
	return typeof u === 'string' && u.toLowerCase().includes('кг');
};

/**
 * Основная цена для карточек/списков (уже отформатированная строка с бэкенда).
 */
const getListPrimaryPrice = item => {
	if (!item) return '';
	const w = isWeightPricedProduct(item);
	if (w) {
		if (item.special_kg) return item.special_kg;
		if (item.special) return item.special;
		if (item.price_kg) return item.price_kg;
		return item.price;
	}
	if (item.special) return item.special;
	return item.price;
};

/** Зачёркнутая «старая» цена для карточек. */
const getListStrikePrice = item => {
	if (!item) return null;
	const w = isWeightPricedProduct(item);
	if (w) {
		if (item.special_kg && item.price_kg) return item.price_kg;
		if (item.special && !item.price_kg) return item.price;
		return null;
	}
	if (item.special) return item.price;
	return null;
};

const packWeightDisplay = item => {
	if (!item || item.weight == null || item.weight === '') return '';
	if (typeof item.weight === 'string') return item.weight.trim();
	return formatWeightWithUnit(item.weight);
};

const getStock = item => {
	if (!item || !item.weight) {
		return {
			quantity: 0,
			stock: 0,
			label: t('stock.unavailable'),
		};
	}

	const PRE_ORDER_STATES = {
		0: 'pre-order',
		999: 'always-yes',
		450: 'by-1c-balance',
	};

	const prop = item.weight.trim().split(/\s+/);
	let cn = parseFloat(String(prop[0]).replace(',', '.'));
	if (!Number.isFinite(cn) || cn <= 0) cn = 1;
	const u1 = prop[1] ? prop[1].toLowerCase() : '';
	if (u1 && (u1.includes('гр') || u1 === 'г')) cn = cn / 1000;

	let stock = {
		quantity: item.quantity,
		stock: Math.floor(item.quantity / cn),
		label: t('stock.availableCount', {
			count: Math.floor(item.quantity / cn),
			unit: item.main_unit,
		}),
	};

	if (['pre-order', 'always-yes'].includes(PRE_ORDER_STATES[item.pre_order])) {
		stock.quantity = item.pre_order;
		stock.stock = item.pre_order;
	}

	if (stock.quantity < cn) {
		stock = {
			quantity: 0,
			stock: 0,
			label: t('stock.unavailable'),
		};
	}
	if (stock.quantity === 999) stock.label = t('stock.available');

	return stock;
};

/**
 * Один общий лимит для корзины: макс. доступно в той же единице, что и в корзине (кг или шт).
 */
function getMaxAvailableInCartUnit(item) {
	if (!item) return 0;
	if (item.pre_order === 999) return 999;
	const stockQty =
		item.stock_qty !== undefined && item.stock_qty !== null
			? Number(item.stock_qty)
			: item.quantity !== undefined && item.quantity !== null
				? Number(item.quantity)
				: 999;
	if (!item.weight) {
		return Number(stockQty);
	}
	const stockItem = {
		quantity: stockQty,
		weight: item.weight,
		unit: item.unit,
		main_unit: item.main_unit,
		pre_order: item.pre_order,
	};
	const stock = getStock(stockItem);
	const max = stock.quantity;
	return Number(max);
}

/**
 * Строка веса с витрины/корзины («0,5 кг», «500 г», «500 гр», «500гр») → килограммы (для API доставки и т.п.).
 * Не использовать сырой parseFloat(weight): для «500 г» он даст 500.
 */
const parseProductWeightToKg = weightRaw => {
	if (weightRaw == null || weightRaw === '') return 0;
	if (typeof weightRaw === 'number') {
		return Number.isFinite(weightRaw) && weightRaw > 0 ? weightRaw : 0;
	}
	const s = String(weightRaw).trim().replace(/,/g, '.');
	const lower = s.toLowerCase();

	const glued = s.match(/^(\d+(?:\.\d+)?)(гр|кг|kg|г)(?:\s|$)/i);
	if (glued) {
		const n = parseFloat(glued[1]);
		if (!Number.isFinite(n) || n <= 0) return 0;
		const u = glued[2].toLowerCase();
		if (u === 'кг' || u === 'kg') return n;
		if (u === 'г' || u === 'гр') return n / 1000;
	}

	const prop = lower.split(/\s+/).filter(Boolean);
	if (!prop.length) return 0;
	let n = parseFloat(prop[0]);
	if (!Number.isFinite(n) || n <= 0) return 0;

	if (prop.length >= 2) {
		const u = prop[1];
		if (u.includes('кг') || u === 'kg') return n;
		if (u.includes('гр') || u === 'г') return n / 1000;
	}

	return n;
};

export {
	getStock,
	getMaxAvailableInCartUnit,
	isWeightPricedProduct,
	getListPrimaryPrice,
	getListStrikePrice,
	packWeightDisplay,
	parseProductWeightToKg,
};
